<template>
    <Container>
        <!-- Header -->
        <div class="w-full flex justify-between items-center my-4">
            <h1 class="text-2xl font-bold text-white">Edit Story</h1>
            <Link href="/backstage/story" class="text-white rounded-md py-2 px-4 bg-nomad-900 hover:bg-nomad-800">
            Back to Stories
            </Link>
        </div>

        <form @submit.prevent="submit" class="max-w-6xl">
            <!-- Section: Basic Information -->
            <div class="bg-nomad-900 rounded-xl p-6 mb-4">
                <h2 class="text-lg font-semibold text-nomad-200 mb-4">Basic Information</h2>
                <div class="grid gap-4">
                    <!-- Title -->
                    <div>
                        <label class="block text-sm font-medium text-nomad-300 mb-2">
                            Title <span class="text-red-400">*</span>
                        </label>
                        <input type="text" v-model="form.title"
                            class="w-full rounded-md bg-nomad-800 border-nomad-700 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Story title" required />
                        <span v-if="form.errors.title" class="text-red-400 text-sm mt-1">{{ form.errors.title }}</span>
                    </div>

                    <!-- Excerpt -->
                    <div>
                        <label class="block text-sm font-medium text-nomad-300 mb-2">
                            Excerpt
                        </label>
                        <textarea v-model="form.excerpt" rows="3"
                            class="w-full rounded-md bg-nomad-800 border-nomad-700 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Short summary (optional)"></textarea>
                        <span v-if="form.errors.excerpt" class="text-red-400 text-sm mt-1">{{ form.errors.excerpt
                        }}</span>
                    </div>

                    <div class="grid md:grid-cols-2 gap-4">
                        <!-- Author -->
                        <div>
                            <label class="block text-sm font-medium text-nomad-300 mb-2">Author</label>
                            <input type="text" v-model="form.author"
                                class="w-full rounded-md bg-nomad-800 border-nomad-700 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                                placeholder="Author name" />
                        </div>

                        <!-- Source Article -->
                        <div>
                            <label class="block text-sm font-medium text-nomad-300 mb-2">Source URL</label>
                            <input type="url" v-model="form.source_article" disabled
                                class="w-full rounded-md bg-nomad-800 border-nomad-700 text-nomad-500 cursor-not-allowed"
                                placeholder="https://..." />
                        </div>
                    </div>

                    <!-- Cover Image -->
                    <div>
                        <ImageUploader v-model="form.image" :image="form.image" />
                    </div>

                    <!-- Published Date -->
                    <div>
                        <label class="block text-sm font-medium text-nomad-300 mb-2">Published Date</label>
                        <input type="datetime-local" v-model="form.published_at"
                            class="w-full rounded-md bg-nomad-800 border-nomad-700 text-white focus:border-nomad-accent focus:ring-nomad-accent" />
                    </div>

                    <!-- Metadata Flags -->
                    <div class="grid md:grid-cols-2 gap-4">
                        <label class="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" v-model="form.image_imported"
                                class="rounded bg-nomad-800 border-nomad-700 text-nomad-accent focus:ring-nomad-accent focus:ring-offset-nomad-900" />
                            <span class="text-sm text-nomad-300">Image Imported</span>
                        </label>
                        <label class="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" v-model="form.optimized_text"
                                class="rounded bg-nomad-800 border-nomad-700 text-nomad-accent focus:ring-nomad-accent focus:ring-offset-nomad-900" />
                            <span class="text-sm text-nomad-300">Text Optimized</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Section: Content Blocks -->
            <div class="bg-nomad-900 rounded-xl p-6 mb-4">
                <h2 class="text-lg font-semibold text-nomad-200 mb-4">Content Blocks</h2>

                <div v-if="form.blocks.length === 0" class="text-center py-8 text-nomad-400">
                    No content blocks. Click below to add one.
                </div>

                <div v-else class="space-y-3">
                    <div v-for="(block, index) in form.blocks" :key="index"
                        class="bg-nomad-800 rounded-lg p-4 border border-nomad-700">
                        <div class="flex justify-between items-start mb-3">
                            <div class="flex items-center gap-2">
                                <span class="text-xs font-semibold text-nomad-accent uppercase">{{ block.type }}</span>
                                <span class="text-xs text-nomad-500">#{{ index + 1 }}</span>
                            </div>
                            <div class="flex gap-2">
                                <button type="button" @click="moveBlockUp(index)" :disabled="index === 0"
                                    class="text-nomad-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-1">
                                    ↑
                                </button>
                                <button type="button" @click="moveBlockDown(index)"
                                    :disabled="index === form.blocks.length - 1"
                                    class="text-nomad-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-1">
                                    ↓
                                </button>
                                <button type="button" @click="removeBlock(index)"
                                    class="bg-red-500 hover:bg-red-600 text-white text-xs rounded px-2 py-1">
                                    Delete
                                </button>
                            </div>
                        </div>

                        <!-- Paragraph Block -->
                        <div v-if="block.type === 'paragraph'">
                            <textarea v-model="block.data.text" rows="6"
                                class="w-full rounded-md bg-nomad-700 border-nomad-600 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                                placeholder="Enter paragraph text..."></textarea>
                        </div>

                        <!-- Heading Block -->
                        <div v-else-if="block.type === 'heading'">
                            <div class="grid md:grid-cols-4 gap-3 mb-2">
                                <select v-model="block.data.level"
                                    class="rounded-md bg-nomad-700 border-nomad-600 text-white focus:border-nomad-accent focus:ring-nomad-accent">
                                    <option value="1">H1</option>
                                    <option value="2">H2</option>
                                    <option value="3">H3</option>
                                    <option value="4">H4</option>
                                </select>
                            </div>
                            <input type="text" v-model="block.data.text"
                                class="w-full rounded-md bg-nomad-700 border-nomad-600 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                                placeholder="Heading text..." />
                        </div>

                        <!-- Image Block -->
                        <div v-else-if="block.type === 'image'">
                            <ImageUploader v-model="block.data.url" :image="block.data.url" />
                            <input type="text" v-model="block.data.text"
                                class="w-full rounded-md bg-nomad-700 border-nomad-600 text-white focus:border-nomad-accent focus:ring-nomad-accent mt-2"
                                placeholder="Text (optional)" />
                        </div>

                        <!-- Audio Block -->
                        <div v-else-if="block.type === 'audio'">
                            <AudioUploader v-model="block.data.url" />
                            <input type="text" v-model="block.data.caption"
                                class="w-full rounded-md bg-nomad-700 border-nomad-600 text-white focus:border-nomad-accent focus:ring-nomad-accent mt-2"
                                placeholder="Caption (optional)" />
                            <div v-if="block.data.url" class="mt-2 text-nomad-400 text-sm">
                                Audio file: {{ block.data.url }}
                            </div>
                        </div>

                        <!-- YouTube Block -->
                        <div v-else-if="block.type === 'youtube'">
                            <input type="text" v-model="block.data.video_id"
                                class="w-full rounded-md bg-nomad-700 border-nomad-600 text-white focus:border-nomad-accent focus:ring-nomad-accent mb-2"
                                placeholder="YouTube Video ID (e.g., dQw4w9WgXcQ)" />
                            <input type="text" v-model="block.data.caption"
                                class="w-full rounded-md bg-nomad-700 border-nomad-600 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                                placeholder="Caption (optional)" />
                            <div v-if="block.data.video_id" class="mt-2">
                                <div class="aspect-video w-full max-w-2xl bg-black rounded overflow-hidden">
                                    <iframe :src="`https://www.youtube.com/embed/${block.data.video_id}`"
                                        class="w-full h-full" frameborder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowfullscreen></iframe>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Add Block Buttons -->
                <div class="flex gap-2 mt-4 justify-center">
                    <button type="button" @click="addBlock('paragraph')"
                        class="text-sm bg-nomad-700 hover:bg-nomad-600 text-white rounded-md py-2 px-3">
                        + Paragraph
                    </button>
                    <button type="button" @click="addBlock('heading')"
                        class="text-sm bg-nomad-700 hover:bg-nomad-600 text-white rounded-md py-2 px-3">
                        + Heading
                    </button>
                    <button type="button" @click="addBlock('image')"
                        class="text-sm bg-nomad-700 hover:bg-nomad-600 text-white rounded-md py-2 px-3">
                        + Image
                    </button>
                    <button type="button" @click="addBlock('audio')"
                        class="text-sm bg-nomad-700 hover:bg-nomad-600 text-white rounded-md py-2 px-3">
                        + Audio
                    </button>
                    <button type="button" @click="addBlock('youtube')"
                        class="text-sm bg-nomad-700 hover:bg-nomad-600 text-white rounded-md py-2 px-3">
                        + YouTube
                    </button>
                </div>
            </div>

            <!-- Form Actions -->
            <div class="flex gap-3">
                <button type="submit"
                    class="bg-nomad-accent hover:bg-nomad-accent/80 text-nomad-900 font-semibold rounded-md py-3 px-6"
                    :disabled="form.processing">
                    <span v-if="form.processing">Saving...</span>
                    <span v-else>Update Story</span>
                </button>
            </div>
        </form>
    </Container>
</template>

<script setup>
import { ref } from 'vue';
import { useForm, usePage, Link } from '@inertiajs/vue3';
import Container from "@/Pages/Backstage/Container.vue";
import ImageUploader from "@/Pages/Backstage/Components/ImageUploader.vue";
import AudioUploader from "@/Pages/Backstage/Components/AudioUploader.vue";

const { story } = usePage().props;

const form = useForm({
    title: story.title || '',
    excerpt: story.excerpt || '',
    author: story.author || '',
    source_article: story.source_article || '',
    image: story.image || '',
    published_at: story.published_at ? story.published_at.slice(0, 16) : '',
    blocks: Array.isArray(story.blocks) ? story.blocks : [],
    image_imported: story.extra?.image_imported || false,
    optimized_text: story.extra?.optimized_text || false,
});

function addBlock(type) {
    const newBlock = {
        type: type,
        data: {}
    };

    if (type === 'paragraph') {
        newBlock.data.text = '';
    } else if (type === 'heading') {
        newBlock.data.text = '';
        newBlock.data.level = 2;
    } else if (type === 'image') {
        newBlock.data.url = '';
        newBlock.data.text = '';
    } else if (type === 'audio') {
        newBlock.data.url = '';
        newBlock.data.caption = '';
    } else if (type === 'youtube') {
        newBlock.data.video_id = '';
        newBlock.data.caption = '';
    }

    form.blocks.push(newBlock);
}

function removeBlock(index) {
    form.blocks.splice(index, 1);
}

function moveBlockUp(index) {
    if (index > 0) {
        const temp = form.blocks[index];
        form.blocks[index] = form.blocks[index - 1];
        form.blocks[index - 1] = temp;
    }
}

function moveBlockDown(index) {
    if (index < form.blocks.length - 1) {
        const temp = form.blocks[index];
        form.blocks[index] = form.blocks[index + 1];
        form.blocks[index + 1] = temp;
    }
}

function submit() {
    // Ensure image blocks have full CDN URL
    form.blocks.forEach(block => {
        if (block.type === 'image' && block.data.url && !block.data.url.includes('.com')) {
            block.data.url = `https://cdn.nomadlyrics.com${block.data.url}`;
        }
    });

    form.put(`/backstage/story/${story.id}`);
}
</script>